import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { PRIORITY_CONDITION_VALUES } from 'dashboard/constants/automation';

/**
 * Composable for handling macro-related functionality
 * @returns {Object} An object containing the getMacroDropdownValues function
 */
export const useMacros = () => {
  const { t } = useI18n();
  const getters = useStoreGetters();

  const labels = computed(() => getters['labels/getLabels'].value);
  const teams = computed(() => getters['teams/getTeams'].value);
  const agents = computed(() => getters['agents/getAgents'].value);
  const whatsappTemplates = computed(
    () => getters['messageTemplates/getApprovedTemplates'].value || []
  );
  const cannedResponses = computed(
    () => getters['cannedResponse/getCannedResponses'].value || []
  );
  const conversationAttributes = computed(
    () => getters['attributes/getConversationAttributes'].value || []
  );
  const contactAttributes = computed(
    () => getters['attributes/getContactAttributes'].value || []
  );

  /**
   * Get dropdown values based on the specified type
   * @param {string} type - The type of dropdown values to retrieve
   * @returns {Array} An array of dropdown values
   */
  const getMacroDropdownValues = type => {
    switch (type) {
      case 'assign_team':
      case 'send_email_to_team':
        return teams.value;
      case 'assign_agent':
        return [{ id: 'self', name: 'Self' }, ...agents.value];
      case 'add_label':
      case 'remove_label':
        return labels.value.map(i => ({
          id: i.title,
          name: i.title,
        }));
      case 'change_priority':
        return PRIORITY_CONDITION_VALUES.map(item => ({
          id: item.id,
          name: t(`MACROS.PRIORITY_TYPES.${item.i18nKey}`),
        }));
      case 'change_status':
        return [
          { id: 'open', name: 'Open' },
          { id: 'pending', name: 'Pending' },
          { id: 'resolved', name: 'Resolved' },
          { id: 'snoozed', name: 'Snoozed' },
        ];
      case 'send_whatsapp_template':
        return whatsappTemplates.value.map(template => ({
          id: template.id,
          name: `${template.name} (${template.language})`,
        }));
      case 'send_response_template':
      case 'send_private_reply':
        return cannedResponses.value.map(response => ({
          id: response.id,
          name: `${response.short_code} (${response.content_type || 'text'})`,
        }));
      case 'update_conversation_custom_attributes':
        return conversationAttributes.value.map(attr => ({
          id: attr.attributeKey,
          name: attr.attributeDisplayName || attr.attributeKey,
          attributeDisplayType: attr.attributeDisplayType,
          attributeValues: attr.attributeValues,
        }));
      case 'update_contact_custom_attributes':
        return contactAttributes.value.map(attr => ({
          id: attr.attributeKey,
          name: attr.attributeDisplayName || attr.attributeKey,
          attributeDisplayType: attr.attributeDisplayType,
          attributeValues: attr.attributeValues,
        }));
      default:
        return [];
    }
  };

  return {
    getMacroDropdownValues,
  };
};
